import { create } from 'zustand'

interface FilesState {
  currentPath: string
  selectedItems: string[]
  lastSelectedPath: string | null
  clipboard: { paths: string[]; isCut: boolean } | null
  viewMode: 'list' | 'grid'
  sortBy: 'name' | 'size' | 'modified' | 'type'
  sortDir: 'asc' | 'desc'
  setCurrentPath: (path: string) => void
  toggleSelection: (path: string) => void
  selectRange: (path: string, allPaths: string[]) => void
  selectAll: (paths: string[]) => void
  clearSelection: () => void
  invertSelection: (paths: string[]) => void
  cut: (paths: string[]) => void
  copy: (paths: string[]) => void
  clearClipboard: () => void
  setViewMode: (mode: 'list' | 'grid') => void
  setSort: (sortBy: 'name' | 'size' | 'modified' | 'type', sortDir: 'asc' | 'desc') => void
}

export const useFilesStore = create<FilesState>((set) => ({
  currentPath: '',
  selectedItems: [],
  lastSelectedPath: null,
  clipboard: null,
  viewMode: 'grid',
  sortBy: 'name',
  sortDir: 'asc',

  setCurrentPath: (path) =>
    set({ currentPath: path, selectedItems: [], lastSelectedPath: null }),

  toggleSelection: (path) =>
    set((state) => {
      const exists = state.selectedItems.includes(path)
      return {
        selectedItems: exists
          ? state.selectedItems.filter((p) => p !== path)
          : [...state.selectedItems, path],
        lastSelectedPath: path,
      }
    }),

  selectRange: (path, allPaths) =>
    set((state) => {
      const from = state.lastSelectedPath ? allPaths.indexOf(state.lastSelectedPath) : 0
      const to = allPaths.indexOf(path)
      if (from === -1 || to === -1) return { selectedItems: [path], lastSelectedPath: path }
      const start = Math.min(from, to)
      const end = Math.max(from, to)
      const rangePaths = allPaths.slice(start, end + 1)
      return {
        selectedItems: rangePaths,
        lastSelectedPath: path,
      }
    }),

  selectAll: (paths) => set({ selectedItems: paths }),

  clearSelection: () => set({ selectedItems: [], lastSelectedPath: null }),

  invertSelection: (paths) =>
    set((state) => ({
      selectedItems: paths.filter((p) => !state.selectedItems.includes(p)),
    })),

  cut: (paths) => set({ clipboard: { paths, isCut: true } }),
  copy: (paths) => set({ clipboard: { paths, isCut: false } }),
  clearClipboard: () => set({ clipboard: null }),
  setViewMode: (mode) => set({ viewMode: mode }),
  setSort: (sortBy, sortDir) => set({ sortBy, sortDir }),
}))